import withdrawApi from "../api/withdraw.api";
import depositApi from "../../deposit/api/deposit.api";
import { DepositFlowResponse } from "../../deposit/models/response/depositFlowResponse";
import localization from "../../../config/localization";
import {
  WithdrawVerificationStatusResponse,
  UserPendingWithdrawalResponse,
  WithdrawalRevertRequest,
  WithdrawFlowResponse,
  UserWithdrawalSummaryResponse,
  WithdrawTypeListResponseList,
  UserBankDetailsList,
  WithdrawFeesTypeListResponseList,
  UserActiveBonusResponse,
  FreeSpinBonusDetail,
  FreeSpinBonusHistoryFromProviderRequest,
  FreeSpinBonusHistoryFromProviderResponse,
  WithdrawType,
  AbcPaymentInitiateWithdrawReuest,
  WithdrawCreateRequest,
  DragonWithdrawCreateRequest,
  CurrencyList,
  UserWalletAddress,
  ExchangeRateData
} from "../models";
import errorModal from "../../../services/errorModal.service";
import { AccessMode } from "../../../models/constants/accessMode";


const withdrawService = {
  getWithdrawVerificationStatus: async (): Promise<WithdrawVerificationStatusResponse | null> => {
    const response = await withdrawApi.getWithdrawVerificationStatus();
    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getUserPendingWithdrawals: async (): Promise<UserPendingWithdrawalResponse | null> => {
    const response = await withdrawApi.getUserPendingWithdrawals();
    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  revertUserWithdrawal: async (
    userPendingWithdrawal: UserPendingWithdrawalResponse
  ): Promise<boolean> => {
    const request: WithdrawalRevertRequest = {
      id: userPendingWithdrawal.id,
      status: userPendingWithdrawal.status,
      isDragon: userPendingWithdrawal.isDragon,
      dragonTransactionId: userPendingWithdrawal.pgTransactionId,
    };
    const response = await withdrawApi.revertUserWithdrawal(request);

    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return response.isSuccess;
  },

  getWithdrawFlowDetails: async (): Promise<WithdrawFlowResponse | null> => {
    const response = await withdrawApi.getWithdrawFlowDetails();
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    else if (response.isSuccess && response.data) {
      return response.data;
    }
    return null;
  },

  getUserWithdrawSummary:
    async (): Promise<UserWithdrawalSummaryResponse | null> => {
      const response = await withdrawApi.getUserWithdrawSummary();
      if (response.isSuccess && response.data) {
        return response.data;
      }
      else if (!response.isSuccess) {
        errorModal.showErrorModal(response.statusCode, response.message);
      }
      return null;
    },

  getWithdrawTypes: async (
    withdrawFlow: string,
    summary: UserWithdrawalSummaryResponse
  ): Promise<WithdrawTypeListResponseList[] | null> => {
    const response = await withdrawApi.getWithdrawTypes(withdrawFlow);
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    if (response.isSuccess && response.data?.details) {
      const validWithdrawTypes = response.data.details.filter((withdrawType: WithdrawTypeListResponseList) =>
        withdrawService.isWithdrawTypeAllowed(withdrawType, summary)
      );
      return validWithdrawTypes;
    }
    return null;
  },

  isWithdrawTypeAllowed: (
    withdrawType: WithdrawTypeListResponseList,
    summary: UserWithdrawalSummaryResponse
  ): boolean => {
    if (!withdrawType || !summary) return false;

    if (!withdrawType.isWithdrawAmount && !withdrawType.isWithdrawTotal && !withdrawType.isAccountAge) {
      return true;
    }

    let isValid = true;

    if (withdrawType.isWithdrawAmount) {
      isValid =
        isValid && (summary.withdrawAmount ?? 0) >= (withdrawType.withdrawAmount ?? 0);
    }

    if (withdrawType.isWithdrawTotal) {
      isValid = isValid && summary.withdrawCount >= withdrawType.withdrawNumber;
    }

    if (withdrawType.isAccountAge) {
      isValid = isValid && summary.daysOld >= withdrawType.accountAge;
    }

    return isValid;
  },

  getDepositFlowDetails: async (): Promise<DepositFlowResponse | null> => {
    const response = await depositApi.getDepositFlowDetails();

    if (response.isSuccess && response.data) {
      return response.data;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getUserBankDetailsList: async (): Promise<UserBankDetailsList[] | null> => {
    const response = await withdrawApi.getUserBankDetailsList();
    if (response.isSuccess && response.data?.details) {
      return response.data.details;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getWithdrawFeesTypesList: async (): Promise<WithdrawFeesTypeListResponseList[] | null> => {
    const response = await withdrawApi.getWithdrawFeesTypesList();
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    else if (response.isSuccess && response.data?.details) {
      return response.data.details;
    }
    return null;
  },

  getUserActiveBonusCount: async (): Promise<UserActiveBonusResponse | null> => {
    const response = await withdrawApi.getUserActiveBonusCount();
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    else if (response.isSuccess && response.data) {
      return response.data;
    }
    return null;
  },

  getUserActiveFreeSpinBonusDetails: async (): Promise<FreeSpinBonusDetail[] | null> => {
    const response = await withdrawApi.getUserActiveFreeSpinBonusDetails();
    if (response.isSuccess && response.data) {
      return response.data.details;
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return null;
  },

  getFreeSpinBonusHistoryFromProvider: async (request: FreeSpinBonusHistoryFromProviderRequest): Promise<FreeSpinBonusHistoryFromProviderResponse | null> => {
    const response = await withdrawApi.getFreeSpinBonusHistoryFromProvider(request);
    if (response.isSuccess && response.data) {
      return response.data;
    }
    return null;
  },

  withdrawSubmit: async (
    selectedWithdrawType: WithdrawTypeListResponseList,
    amount: number,
    receivedAmount: number,
    feeAmount: number,
    selectedUserBank: UserBankDetailsList | null,
    selectedFeeType: WithdrawFeesTypeListResponseList | null,
    username: string | null,
    isshowactivebonuscancelmodal: boolean,
    removedFreeSpinBonusIdList: string[],
    withdrawFlow: string,
    accessModeSource: string
  ): Promise<{ success: boolean; message?: string }> => {

    if (selectedWithdrawType.name && selectedWithdrawType.name.toLowerCase() === WithdrawType.PayzEasy) {
      if (receivedAmount <= 0) {
        return {
          success: false,
          message: localization.sql_error_amount_after_fee_should_not_zero,
        };
      }

      if (receivedAmount < 100) {
        return {
          success: false,
          message: localization.sql_error_amount_after_fee_should_not_hundred,
        };
      }
    }

    if (selectedWithdrawType.name && (selectedWithdrawType.name.toLowerCase() === WithdrawType.ABC ||
      selectedWithdrawType.name.toLowerCase() === WithdrawType.ABCUTR ||
      selectedWithdrawType.name.toLowerCase() === WithdrawType.ABCUTRTWO ||
      selectedWithdrawType.name.toLowerCase() === WithdrawType.ABCUTRTHREE ||
      selectedWithdrawType.name.toLowerCase() === WithdrawType.ABCUTRFOUR ||
      selectedWithdrawType.name.toLowerCase() === WithdrawType.ABCUTRFIVE)) {
      if (receivedAmount >= 50) {
        const request: AbcPaymentInitiateWithdrawReuest = {
          account_holder_name: null,
          account_number: selectedUserBank?.accountnumber ?? null,
          bank_name: selectedUserBank?.accountnumber ?? null,
          ifsc: selectedUserBank?.ifsc ?? "",
          amount: receivedAmount,
          PaymentMethod: selectedWithdrawType.name ?? "",
          Username: username,
        };

        const response = await withdrawApi.abcWithdrawInitiate(request);

        if (!response.isSuccess) {
          errorModal.showErrorModal(response.statusCode, localization.REQUEST_FROM_BLOCKED_USER);
          return { success: false, message: localization.REQUEST_FROM_BLOCKED_USER };
        }
        else if (response.isSuccess && response.data) {
          const request: WithdrawCreateRequest = {
            amount: amount,
            userBankId: selectedUserBank?.userbankid ?? null,
            feeTypeId: selectedFeeType?.id ?? null,
            isWithdrawFree: feeAmount === 0 ? true : false,
            isCancelActiveBonus: isshowactivebonuscancelmodal,
            realAmount: receivedAmount,
            Fee: feeAmount,
            feeType: selectedFeeType?.displayname ?? null,
            withdrawTypeId: selectedWithdrawType.id,
            isWaiver: false,
            processingTime: selectedFeeType?.processingtime ?? 0,
            abcReferenceId: response.data.reference_id ?? null,
            merchantTransactionId: response.data.abcMerchantReferenceId ?? null,
            removedFreeSpinBonusIdList: removedFreeSpinBonusIdList,
            accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
            withdrawFlow: withdrawFlow,
          };
          return await withdrawService.WithdrawInsert(request);
        }
      }
      else {
        if (receivedAmount <= 0) {
          return { success: false, message: localization.sql_error_amount_after_fee_should_not_zero };
        }
        if (receivedAmount < 50) {
          return { success: false, message: localization.sql_error_amount_after_fee_should_not_Fifty };
        }
      }
    }
    else if (selectedWithdrawType.isDragon) {
      if (receivedAmount >= 10) {
        const request: DragonWithdrawCreateRequest = {
          amount: amount,
          ppaName: selectedWithdrawType.ppaName,
          baseUrl: window.location.origin,
          accountNumber: selectedUserBank?.accountnumber ?? null,
          ifscNumber: selectedUserBank?.ifsc ?? null,
          username: username ?? "",
        }

        const response = await withdrawApi.dragonWithdrawCreate(request);
        if (!response.isSuccess) {
          errorModal.showErrorModal(response.statusCode, response.message);
          return { success: false, message: response.message };
        }
        if (response.isSuccess && response.data) {
          const request: WithdrawCreateRequest = {
            amount: amount,
            userBankId: selectedUserBank?.userbankid ?? null,
            feeTypeId: selectedFeeType?.id ?? null,
            isWithdrawFree: feeAmount === 0 ? true : false,
            isCancelActiveBonus: isshowactivebonuscancelmodal,
            realAmount: receivedAmount,
            Fee: feeAmount,
            feeType: selectedFeeType?.displayname ?? null,
            withdrawTypeId: selectedWithdrawType.id,
            isWaiver: false,
            processingTime: selectedFeeType?.processingtime ?? 0,
            merchantTransactionId: response.data.data.merchantTxnId ?? null,
            pgTransactionId: response.data.data.dragonTxnId.toString() ?? null,
            removedFreeSpinBonusIdList: removedFreeSpinBonusIdList,
            accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
            withdrawFlow: withdrawFlow,
          };
          return await withdrawService.WithdrawInsert(request);
        }
      }
      else {
        if (receivedAmount <= 0) {
          return { success: false, message: localization.sql_error_amount_after_fee_should_not_zero };
        }
        if (receivedAmount < 10) {
          return { success: false, message: localization.sql_error_amount_after_fee_should_not_ten };
        }
      }
    }
    else {
      const request: WithdrawCreateRequest = {
        amount: amount,
        userBankId: selectedUserBank?.userbankid ?? null,
        feeTypeId: selectedFeeType?.id ?? null,
        isWithdrawFree: feeAmount === 0 ? true : false,
        isCancelActiveBonus: isshowactivebonuscancelmodal,
        realAmount: receivedAmount,
        Fee: feeAmount,
        feeType: selectedFeeType?.displayname ?? null,
        withdrawTypeId: selectedWithdrawType.id,
        isWaiver: false,
        processingTime: selectedFeeType?.processingtime ?? 0,
        removedFreeSpinBonusIdList: removedFreeSpinBonusIdList,
        accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
        withdrawFlow: withdrawFlow,
      };
      return await withdrawService.WithdrawInsert(request);
    }

    return { success: false, message: "Invalid withdraw type" };
  },

  WithdrawInsert: async (request: WithdrawCreateRequest): Promise<{ success: boolean; message?: string }> => {
    const response = await withdrawApi.withdrawInsert(request);
    if (response.isSuccess) {
      return { success: true };
    }
    else if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return { success: false, message: response.message };
  },

  getCryptoCurrenciesList: async (): Promise<CurrencyList[] | null> => {
    const response = await withdrawApi.getCryptoCurrenciesList();
    if (response.isSuccess && response.data?.currencies_list) {
      return response.data.currencies_list;
    }
    return null;
  },

  getUserWalletAddress: async (): Promise<UserWalletAddress[] | null> => {
    const response = await withdrawApi.getUserWalletAddress();

    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    else if (response.isSuccess && response.data?.details) {
      return response.data.details;
    }
    return null;
  },

  getExchangeRates: async (amount: number): Promise<ExchangeRateData[] | null> => {
    const response = await withdrawApi.getExchangeRates(amount);
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    else if (response.isSuccess && response.data?.data) {
      return response.data.data;
    }
    return null;
  },

  sendMobileVerificationCodeForWalletAddress: async (): Promise<any> => {
    const response = await withdrawApi.sendMobileVerificationCodeForWalletAddress();
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return response;
  },

  verifyMobileVerificationCodeForWalletAddress: async (code: string): Promise<any> => {
    const response = await withdrawApi.verifyMobileVerificationCodeForWalletAddress(code);
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return response;
  },

  walletAddressSubmit: async (walletaddress: string): Promise<any> => {
    const response = await withdrawApi.walletAddressSubmit(walletaddress);
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
    }
    return response;
  },

  cryptoWithdrawInitiate: async (
    selectedWithdrawType: WithdrawTypeListResponseList,
    amount: number,
    receivedAmount: number,
    feeAmount: number,
    selectedFeeType: WithdrawFeesTypeListResponseList | null,
    isshowactivebonuscancelmodal: boolean,
    removedFreeSpinBonusIdList: string[],
    withdrawFlow: string,
    selectedCryptoCurrency: CurrencyList | null,
    walletAddress: string | null,
    accessModeSource: string
  ): Promise<{ success: boolean; message?: string }> => {
    const response = await withdrawApi.cryptoWithdrawInitiate(amount);
    if (!response.isSuccess) {
      errorModal.showErrorModal(response.statusCode, response.message);
      return { success: false, message: response.message };
    }
    else if (response.isSuccess && response.data) {
      const request: WithdrawCreateRequest = {
        amount: amount,
        feeTypeId: selectedFeeType?.id ?? null,
        isWithdrawFree: feeAmount === 0 ? true : false,
        isCancelActiveBonus: isshowactivebonuscancelmodal,
        realAmount: receivedAmount,
        Fee: feeAmount,
        feeType: selectedFeeType?.displayname ?? null,
        withdrawTypeId: selectedWithdrawType.id,
        isWaiver: false,
        processingTime: selectedFeeType?.processingtime ?? 0,
        abcReferenceId: response.data.transfer_reference_id ?? null,
        merchantTransactionId: response.data.abcMerchantReferenceId ?? null,
        removedFreeSpinBonusIdList: removedFreeSpinBonusIdList,
        walletAdddress: walletAddress,
        cryptoCurrency: selectedCryptoCurrency?.crypto_currency,
        networkId: selectedCryptoCurrency?.network_id,
        accessMode: accessModeSource.toLowerCase() === AccessMode.App.toLowerCase() ? 2 : 1,
        withdrawFlow: withdrawFlow,
      };
      return await withdrawService.WithdrawInsert(request);
    }
    return { success: false, message: "Invalid withdraw type" };
  },

};

export default withdrawService;
